import { partialDateToValue } from 'neuro-utils';
import { fetchWithOptions } from '../../../utility/fetch';
import { parseJWTFromCookie } from '../../../utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from '../../../utility/logger';
import { sortAndFilterArchives } from './helpers';

import type { IArchiveObject, IClinicalTextObject } from './helpers';
import type { INeuroArchiveTemplate, INeuroTemplatedClinicalArchiveDocument } from 'neuro-data-structures';

const apiVersion = 'v3';
const archiveCreateMainAuthorHeaderName = 'author-user-id';
const secondaryAuthorRoleCode = 'KIR';

// Get list of templates
export const getTemplateList = async (
  org: string,
  module: Platform,
  docType: string,
  setError?: (e: string) => void,
): Promise<INeuroArchiveTemplate[] | Error> => {
  return await fetchWithOptions(
    `/api/clinicalArchive/${apiVersion}/templates/${org}/${module.toUpperCase()}/${docType}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .catch((error: unknown) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET clinical archive template list'));
      setError && setError('template');
      return error;
    });
};

/** Create a new draft (unsent) archive text document. */
export const createDraftArchiveDocument = async (
  orgId: string,
  diseaseModule: string,
  archiveType: string,
  authorUserId: string,
  secondaryAuthorUserId: string | null,
  templateId: string,
  startDate: PartialDate,
  endDate: PartialDate | null,
  setError?: (e: string) => void,
) => {
  const startTimeStamp = partialDateToValue(startDate);
  const endTimeStamp = endDate ? partialDateToValue(endDate) : undefined;

  const dataStartTimestamp = `?dataStartTimestamp=${startTimeStamp}`;
  const dataEndTimestamp = endTimeStamp ? `&dataEndTimestamp=${endTimeStamp}` : '';

  try {
    const responseDraftInit = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/archive/${orgId}/${templateId}${dataStartTimestamp}${dataEndTimestamp}`,
      {
        neurojwt: parseJWTFromCookie(),
        [archiveCreateMainAuthorHeaderName]: authorUserId,
      },
      { method: 'GET' },
    );
    if (responseDraftInit.status !== 200) {
      throw new Error(`Error creating archive doc: Upstream responded with status ${responseDraftInit.status}`);
    }
    const createdClinicalDocument: INeuroTemplatedClinicalArchiveDocument = await responseDraftInit.json();

    if (secondaryAuthorUserId) {
      const responsePatchSecondaryAuthor = await fetchWithOptions(
        `/api/clinicalArchive/${apiVersion}/authors/${orgId}/${diseaseModule}/${archiveType}/${createdClinicalDocument.id}`,
        {
          neurojwt: parseJWTFromCookie(),
        },
        {
          method: 'PATCH',
          body: JSON.stringify([
            {
              userId: secondaryAuthorUserId,
              authorRole: secondaryAuthorRoleCode,
              timestamp: Date.now(),
            },
          ]),
        },
      );
      if (responsePatchSecondaryAuthor.status !== 200) {
        throw new Error(
          `Error patching secondary author: Upstream responded with status ${responsePatchSecondaryAuthor.status}`,
        );
      }
    }

    return createdClinicalDocument;
  } catch (err: any) {
    makeLog(
      Loggable.BugActionable,
      err instanceof Error ? err : new Error('strange error'),
      new ActionableErrorContext(null, 'create clinical archive draft'),
    );
    if (setError) setError('createNew');
    return undefined;
  }
};

// Get list of archive documents
export const getArchiveDocumentList = async (
  org: string,
  platform: Platform,
  setError?: (e: string) => void,
): Promise<IArchiveObject[] | Error> => {
  try {
    const res = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/list/${org}/${platform.toUpperCase()}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (res.status === 200) {
      const data = (await res.json()) as INeuroTemplatedClinicalArchiveDocument[];
      return sortAndFilterArchives(data);
    } else throw new Error('unexpected status ' + res.status);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET clinical archive document list'));
    setError?.('list');
    if (error instanceof Error) return error;
    return new Error(String(error));
  }
};

// Send archive document
export const sendArchiveDocument = async (
  org: string,
  module: string,
  docType: string,
  clinicalDocument: IArchiveObject,
  startDate: PartialDate,
  endDate: PartialDate | null,
  reason: string | null,
  kantaDelayInput: number | null,
): Promise<'sent' | 'notSent' | false> => {
  const startTimeStamp = partialDateToValue(startDate);
  const endTimeStamp = endDate ? partialDateToValue(endDate) : undefined;

  const textContentVersion = `?textContentVersion=${clinicalDocument.textContentVersion}`;
  const dataStartTimestamp = `&dataStartTimestamp=${startTimeStamp}`;
  const dataEndTimestamp = endTimeStamp ? `&dataEndTimestamp=${endTimeStamp}` : '';
  const kantaDelayDays = typeof kantaDelayInput === 'number' ? `&kantaDelayDays=${kantaDelayInput}` : '';

  const res: Response = await fetchWithOptions(
    `/api/clinicalArchive/${apiVersion}/archive/${org}/${module.toUpperCase()}/${docType}/${
      clinicalDocument.id
    }${textContentVersion}${dataStartTimestamp}${dataEndTimestamp}${kantaDelayDays}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: reason ?? 'noReasonForFirstRevision' },
  );
  const wasSent: boolean = res.headers.get('wassent') === 'true';
  if (res.ok) {
    if (wasSent) return 'sent' as const;
    return 'notSent' as const;
  } else throw new ClinicalArchiveAPIError(wasSent, await res.arrayBuffer(), res.status, 'Could not POST document');
};

export class ClinicalArchiveAPIError extends Error {
  public readonly wasSent: boolean;
  public readonly notOkPayload: ArrayBuffer;
  public readonly statusCode: number;

  constructor(
    wasSent: boolean,
    notOkPayload: ArrayBuffer,
    statusCode: number,
    ...superArgs: ConstructorParameters<typeof Error>
  ) {
    super(...superArgs);
    this.wasSent = wasSent;
    this.notOkPayload = notOkPayload;
    this.statusCode = statusCode;
  }

  public failureMessageDisplay(): null | string {
    try {
      const decoder = new TextDecoder('utf-8');
      const str = decoder.decode(this.notOkPayload);
      const deserialized: unknown = JSON.parse(str);

      if (
        typeof deserialized !== 'object' ||
        !deserialized ||
        !('failureMessageDisplay' in deserialized) ||
        typeof deserialized.failureMessageDisplay !== 'string'
      ) {
        return null;
      } else {
        return deserialized.failureMessageDisplay;
      }
    } catch {
      return null;
    }
  }
}

// Create new revision for an existing archive document
export const createNewArchiveRevision = async (
  org: string,
  module: string,
  docType: string,
  document: IArchiveObject,
  setError?: (e: string) => void,
): Promise<INeuroTemplatedClinicalArchiveDocument | null> => {
  try {
    const res = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/amend/${org}/${module.toUpperCase()}/${docType}/${document.id}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (res.status === 200) {
      const obj = await res.json();
      return obj as INeuroTemplatedClinicalArchiveDocument;
    } else {
      throw new Error('unexpected status ' + res.status);
    }
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'create new clinical archive revision'));
    setError?.('newRevision');
    return null;
  }
};

/*
 * Update the 'custom' text content in the archive document
 */
export const updateArchiveDocumentText = async (
  org: string,
  module: string,
  docType: string,
  document: IArchiveObject,
  textChanges: IClinicalTextObject[],
  setError?: (e: string) => void,
): Promise<boolean> => {
  if (
    textChanges.every(
      t => t.text === undefined && t.dataStartTimestamp === undefined && t.dataEndTimestamp === undefined,
    )
  ) {
    return false;
  }

  try {
    const res = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/archive/${org}/${module.toUpperCase()}/${docType}/${
        document.id
      }?textContentVersion=${document.textContentVersion}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'PATCH', body: JSON.stringify(textChanges) },
    );
    if (res.status === 200) return true;
    else throw new Error('unexpected status ' + res.status);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PATCH clinical archive document text'));
    setError && setError('textUpdate');
    return false;
  }
};

export const deleteArchiveDocumentDraft = async (
  org: string,
  module: string,
  docType: string,
  document: IArchiveObject,
  setError?: (e: string) => void,
): Promise<boolean> => {
  try {
    const res = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/archive/${org}/${module.toUpperCase()}/${docType}/${document.id}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'DELETE' },
    );
    if (res.status === 200) return true;
    else throw new Error('unexpected status ' + res.status);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE clinical archive document draft'));
    setError?.('deletion');
    return false;
  }
};

export const deleteArchiveDocument = async (
  org: string,
  module: string,
  docType: string,
  document: IArchiveObject,
  reason: string,
  setError?: (e: string) => void,
): Promise<boolean> => {
  try {
    const res = await fetchWithOptions(
      `/api/clinicalArchive/${apiVersion}/delete/${org}/${module.toUpperCase()}/${docType}/${document.archiveId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'DELETE', body: reason },
    );
    if (res.status === 200) return true;
    else throw new Error('unexpected status ' + res.status);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE clinical archive document'));
    setError?.('deletion');
    return false;
  }
};

export const refreshArchiveDocument = async (
  org: string,
  module: string,
  docType: string,
  document: IArchiveObject,
  startDate: PartialDate | number,
  endDate: PartialDate | number | null,
  upTextContentVersion?: boolean,
  setError?: (e: string) => void,
): Promise<INeuroTemplatedClinicalArchiveDocument | null> => {
  const startTimeStamp = Array.isArray(startDate) ? partialDateToValue(startDate) : startDate;
  const endTimeStamp = endDate && Array.isArray(endDate) ? partialDateToValue(endDate) : endDate;

  const textContentVersion = `?textContentVersion=${document.textContentVersion + (upTextContentVersion ? 1 : 0)}`;
  const dataStartTimestamp = `&dataStartTimestamp=${startTimeStamp}`;
  const dataEndTimestamp = endTimeStamp ? `&dataEndTimestamp=${endTimeStamp}` : '';

  return fetchWithOptions(
    `/api/clinicalArchive/${apiVersion}/refresh/${org}/${module.toUpperCase()}/${docType}/${
      document.id
    }${textContentVersion}${dataStartTimestamp}${dataEndTimestamp}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((error: unknown) => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'refresh clinical archive document'));
      setError && setError('refresh');
      return null;
    });
};
